import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';

import NoAccess from '../../NoAccess';
import Button from '../../../../../shared/Button';
import Input from '../../../../../shared/Input';
import CaseAccordion from '../../CaseAccordion';
import AlertModal from '../../../../AlertModal';

import preopIcon from '../../../../../../assets/preop_icon.svg';
import calendarIcon from '../../../../../../assets/calendar_icon.svg';

import { updateCase } from '../../../../../../actions/casesActions';

import { caseConstants } from '../../../../../../constants';
import { roleNames } from '../../../../../../constants/userRoles';
import { fileTypes } from '../../../../../../constants/cases';

const { fileStatuses, caseCMFExtendedNotifications, statuses, procedureFlows, matorthoProcedureFlows } = caseConstants;
const { GLOBAL_ADMIN, COUNTRY_ADMIN } = roleNames;

const getImageSrc = (procedureFlow) => {
  if (procedureFlow === procedureFlows.ONCOL) {
    return 'Pre-Op_Front_Page_drop_ONCOL_planning.png';
  }
  if (procedureFlow === procedureFlows.CUSTOM_EXTENDED) {
    return 'Pre-Op_Front_Page_drop_Custom_planning.png';
  }
  if (procedureFlow === procedureFlows.DOUBLE_LEVEL_OSTEOTOMY) {
    return 'Pre-Op_Front_Page_drop_Double_level_planning.png';
  }
  if (procedureFlow === procedureFlows.VILEX) {
    return 'Pre-Op_Front_Page_drop_VILEX_planning.png';
  }
  if (matorthoProcedureFlows.includes(procedureFlow)) {
    return 'Pre-Op_Front_Page_drop_Matortho_planning.png';
  }
  return 'Pre-Op_Front_Page_drop_CMF_planning.png';
};

const CmfExtendedStep4 = ({ classes, onClose, activeCase, planReport, updateCase, userRole, userId }) => {
  const {
    patientInfo,
    date,
    hospital,
    procedureName,
    hardware: caseHardware,
    manufacturing: caseManufacturing,
  } = activeCase;

  const theme = useTheme();

  const [status, setStatus] = useState(fileStatuses.APPROVED);
  const [notes, setNotes] = useState('');
  const [closeCaseModalOpen, setCloseCaseModalOpen] = useState(false);
  const [hardwareList, setHardwareList] = useState(caseHardware ? caseHardware : []);
  const [manufacturingList, setManufacturingList] = useState(caseManufacturing ? caseManufacturing : []);
  const showApprovalNote = [procedureFlows.CUSTOM_EXTENDED, procedureFlows.VILEX, ...matorthoProcedureFlows].includes(
    activeCase.procedureFlow
  );
  const isMatorthoPlanOnly =
    matorthoProcedureFlows.includes(activeCase.procedureFlow) && activeCase.skipPSI && activeCase.planningFileRequired;

  const handleSubmit = (completeCase = false) => {
    if (completeCase && (!isMatorthoPlanOnly || status !== fileStatuses.APPROVED)) {
      return;
    }

    const now = moment().format();
    const activity = [...activeCase.activity];
    const files = [...activeCase.files];
    const planReportFile = files.find((file) => file.id === planReport.id);

    if (!planReportFile) {
      return;
    }

    let rejected = false;

    if (status === fileStatuses.REJECTED) {
      activity.unshift({ ...caseCMFExtendedNotifications[4].reject, date: now, userId });
      rejected = true;
    } else {
      activity.unshift({ ...caseCMFExtendedNotifications[4].success, date: now, userId });
      rejected = false;
    }

    if (completeCase) {
      activity.unshift({ ...caseCMFExtendedNotifications[4].close, date: now, userId });
    }

    planReportFile.status = status;

    const data = {
      ...activeCase,
      files,
      step: completeCase ? 12 : rejected ? 3 : 5,
      activity,
      completionDates: {
        ...activeCase.completionDates,
        step4: now,
        ...(completeCase ? { step12: now } : {}),
      },
      completedBy: {
        ...activeCase.completedBy,
        step4: userId,
        ...(completeCase ? { step12: userId } : {}),
      },
      notes: { ...activeCase.notes, step4: notes.trim() },
      status: completeCase ? statuses.completed : rejected ? statuses.rejected : statuses.inProgress,
    };

    updateCase(data, rejected).then(() => {
      onClose();
    });
  };

  return (
    <Fragment>
      <div className="case-step-container">
        <div className="case-step-content">
          <div className="case-step-row">
            <div className="column">
              <div className="modal-item-circle">4</div>
            </div>
            <div className="modal-item-content">
              <div className="m-b-lg">Planning Approval</div>
              {activeCase.access ? (
                <Fragment>
                  {!!planReport && (
                    <div className="modal-item-block margin-0">
                      <div className="secondary font-size-sm border-bottom p-b-md m-b-md">
                        Please review the proposed plan report below and approve or reject with supporting notes
                      </div>
                      <div className="d-flex space-between">
                        <div>
                          {patientInfo.firstName} {patientInfo.lastName}
                        </div>
                        <div className="d-flex">
                          <img src={calendarIcon} alt="calendar-icon" height={15} className="m-r-sm" />
                          {moment(date).format('DD/MM/YYYY')}
                        </div>
                      </div>
                      <div className="d-flex space-between m-b-lg">
                        <div>{hospital}</div>
                        <div className={classes.primary} style={{ color: theme.palette.primary.main }}>
                          {procedureName}
                        </div>
                      </div>
                      <div className="block-header">Attached plan report</div>
                      <div className="preop-image-container d-flex flex-center">
                        <a
                          href={planReport.downloadLink}
                          target="_blank"
                          className={`${classes.primary} text-center`}
                          style={{ color: theme.palette.primary.main }}
                          rel="noopener noreferrer"
                          download
                        >
                          <img
                            src={getImageSrc(activeCase.procedureFlow)}
                            alt="preop"
                            style={{ maxHeight: 300 }}
                            width="100%"
                          />
                          <div className=" text-center pointer">Click to view</div>
                        </a>
                      </div>
                      <div className="d-flex p-t-md p-b-md flex-center">
                        <img src={preopIcon} alt="preop-icon" height={20} className="m-r-sm" />
                        <span>{planReport.name}</span>
                      </div>
                      <div className="radio-group-container d-flex flex-center">
                        <RadioGroup
                          row
                          aria-label="preop-radio"
                          name="preop-radio"
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          <FormControlLabel
                            value={fileStatuses.APPROVED}
                            control={<Radio color="primary" />}
                            label="Approve"
                          />
                          <FormControlLabel
                            value={fileStatuses.REJECTED}
                            control={<Radio color="primary" />}
                            label="Reject"
                          />
                        </RadioGroup>
                      </div>
                      {status === fileStatuses.REJECTED && (
                        <Input
                          id="notes"
                          type="text"
                          fullWidth
                          multiline
                          label="Notes"
                          placeholder="Add reason for reject here"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                        />
                      )}
                      {showApprovalNote && status === fileStatuses.APPROVED && (
                        <Input
                          id="approval-note"
                          type="text"
                          fullWidth
                          multiline
                          label="Approval Note"
                          placeholder="Add an optional note for the engineer"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                        />
                      )}
                    </div>
                  )}
                </Fragment>
              ) : (
                <NoAccess />
              )}
            </div>
          </div>
          {(userRole === GLOBAL_ADMIN || userRole === COUNTRY_ADMIN || !activeCase.access) && (
            <CaseAccordion currentStep={4} />
          )}
        </div>
        <div className="case-modal-actions">
          <div className="secondary pointer" onClick={onClose}>
            Close
          </div>
          {isMatorthoPlanOnly && status === fileStatuses.APPROVED ? (
            <Button
              text="Approve & Close Case"
              color="primary"
              onClick={() => setCloseCaseModalOpen(true)}
              disabled={!activeCase.access}
            />
          ) : (
            <Button
              text="Submit"
              color="primary"
              onClick={() => handleSubmit(false)}
              disabled={!activeCase.access || (status === fileStatuses.REJECTED && !notes.trim())}
            />
          )}
        </div>
      </div>
      <AlertModal
        open={closeCaseModalOpen}
        onClose={() => setCloseCaseModalOpen(false)}
        onSubmit={() => {
          setCloseCaseModalOpen(false);
          handleSubmit(true);
        }}
        title="Approve and close this case?"
        text="The planning report will be approved and the case will be completed. Steps 5–11 will be skipped."
        submitText="Approve & Close"
      />
    </Fragment>
  );
};

CmfExtendedStep4.propTypes = {
  activeCase: PropTypes.object,
  planReport: PropTypes.object,
  onClose: PropTypes.func,
  updateCase: PropTypes.func,
  goToFiles: PropTypes.func,
};

const mapStateToProps = (state) => {
  const activeCase = state.cases.activeCase;
  const procedure = state.procedures.list.find((procedure) => procedure.id === activeCase.procedure);
  const products = procedure ? procedure.products : [];

  return {
    activeCase,
    planReport: state.cases.activeCase.files?.find((file) => file.type === fileTypes.planReport),
    users: state.users.list,
    userRole: state.user.currentUser.role,
    products,
    userId: state.user.currentUser.uid,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateCase: (newCase, rejected) => dispatch(updateCase(newCase, 4, null, rejected)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CmfExtendedStep4);
